import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Button,
    Card,
    CardContent,
    CardActions,
    Grid,
    CircularProgress,
    Alert,
    Divider,
    TextField
} from "@mui/material";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import AutoDeleteIcon from "@mui/icons-material/AutoDelete";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import swal from "sweetalert";
import axios from "axios";
import { DashboardLayout } from "../../components/dashboard-layout";
import { ThemeProvider } from "@mui/material/styles";
import { theme } from "../../theme";
import "./account.css";
import { isFeatureEnabled } from "../../utils/serverFeatures";

axios.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem("user");
        if (token) config.headers["Authorization"] = `Bearer ${token}`;
        return config;
    },
    (error) => Promise.reject(error)
);

function Cleanup() {
    const [loading, setLoading] = useState({ gameHistory: false, promoUsage: false, all: false, goLive: false, userCleanup: false });
    const [result, setResult] = useState(null);
    const [targetUsername, setTargetUsername] = useState("");
    const cleanupSupported = isFeatureEnabled("cleanup");

    const profile = JSON.parse(localStorage.getItem("profile") || "{}");
    const isSuperAdmin = profile.iduser === 1;

    const handleAction = async (type, label) => {
        if (!cleanupSupported) {
            setResult({
                type: "warning",
                message: "Server hiện tại chưa hỗ trợ endpoint cleanup."
            });
            return;
        }

        if (type === "userCleanup" && !targetUsername.trim()) {
            swal("Lỗi!", "Vui lòng nhập Username người chơi cần xóa!", "error");
            return;
        }

        const confirmed = await swal({
            title: "Xác nhận dọn dẹp",
            text: type === "userCleanup"
                ? `Bạn có chắc muốn xóa tài khoản '${targetUsername}' và mọi lịch sử liên quan? Hành động này KHÔNG THỂ hoàn tác!`
                : `Bạn có chắc muốn ${label}? Hành động này KHÔNG THỂ hoàn tác!`,
            icon: "warning",
            buttons: ["Hủy", "Xác nhận xóa"],
            dangerMode: true
        });
        if (!confirmed) return;

        setLoading((prev) => ({ ...prev, [type]: true }));
        setResult(null);

        try {
            let res;
            if (type === "userCleanup") {
                res = await axios.post(`${process.env.REACT_APP_API_URL}/cleanup/user`, { username: targetUsername });
            } else {
                const endpoint = type === "gameHistory" ? "game-history" : type === "promoUsage" ? "promo-usage" : type === "goLive" ? "go-live" : "all";
                res = await axios.post(`${process.env.REACT_APP_API_URL}/cleanup/${endpoint}`);
            }

            if (res.data.success) {
                setResult({ type: "success", message: res.data.message });
                swal("Thành công!", res.data.message, "success");
                if (type === "userCleanup") setTargetUsername("");
            } else {
                setResult({ type: "error", message: res.data.message });
                swal("Lỗi!", res.data.message, "error");
            }
        } catch (err) {
            const msg = err?.response?.data?.message || "Có lỗi xảy ra!";
            setResult({ type: "error", message: msg });
            swal("Lỗi!", msg, "error");
        } finally {
            setLoading((prev) => ({ ...prev, [type]: false }));
        }
    };

    const cards = [
        {
            key: "gameHistory",
            icon: <DeleteSweepIcon sx={{ fontSize: 48, color: "#f44336" }} />,
            title: "Xóa lịch sử chơi game",
            description:
                "Xóa toàn bộ lịch sử đặt cược của tất cả người chơi trên tất cả sảnh game (Tài Xỉu, Xóc Đĩa, Xổ Số, Tài Xỉu MD5, Greedy, CatDog). Dữ liệu sẽ trở về trạng thái ban đầu.",
            label: "xóa toàn bộ lịch sử chơi game",
            btnLabel: "Xóa lịch sử game",
            btnColor: "error"
        },
        {
            key: "promoUsage",
            icon: <CardGiftcardIcon sx={{ fontSize: 48, color: "#ff9800" }} />,
            title: "Reset lịch sử dùng Giftcode",
            description:
                "Reset số lượt sử dụng của tất cả giftcode về lại giá trị ban đầu (max). Đồng thời xóa danh sách mã đã dùng trong từng tài khoản, giúp mọi người có thể dùng lại giftcode.",
            label: "reset toàn bộ lịch sử giftcode",
            btnLabel: "Reset Giftcode",
            btnColor: "warning"
        },
        {
            key: "userCleanup",
            icon: <PersonRemoveIcon sx={{ fontSize: 48, color: "#2196f3" }} />,
            title: "Xóa 1 người chơi & Lịch sử",
            description:
                "Nhập Username để xóa tài khoản người chơi đó, đồng thời quét sạch lịch sử cược, lịch sử giao dịch nạp/rút/chuyển khoản, lịch sử chat, tiết kiệm và hoa hồng đại lý liên quan.",
            label: "xóa người chơi và toàn bộ lịch sử liên quan",
            btnLabel: "Xóa người chơi",
            btnColor: "info",
            hasInput: true
        },
        {
            key: "all",
            icon: <AutoDeleteIcon sx={{ fontSize: 48, color: "#9c27b0" }} />,
            title: "Dọn dẹp tất cả",
            description:
                "Thực hiện đồng thời cả hai tác vụ trên: xóa lịch sử chơi game VÀ reset giftcode. Hệ thống sẽ được đưa về trạng thái sạch như mới cài đặt.",
            label: "dọn dẹp toàn bộ hệ thống",
            btnLabel: "Dọn dẹp tất cả",
            btnColor: "secondary"
        },
        {
            key: "goLive",
            icon: <SettingsBackupRestoreIcon sx={{ fontSize: 48, color: "#4caf50" }} />,
            title: "Dọn dẹp chạy chính thức",
            description:
                "Xóa sạch toàn bộ tài khoản người chơi (giữ lại admin), lịch sử nạp rút, lịch sử cược, giftcode và reset các thông số/tiền của Admin để đưa hệ thống vào hoạt động chính thức.",
            label: "dọn dẹp sạch toàn bộ dữ liệu để chuẩn bị chạy chính thức",
            btnLabel: "Dọn dẹp chạy chính thức",
            btnColor: "success"
        }
    ];

    return (
        <ThemeProvider theme={theme}>
            <DashboardLayout>
                <Box component="main" sx={{ flexGrow: 1, py: 4 }}>
                    <Container maxWidth="lg">
                        <Typography variant="h4" sx={{ mb: 1, fontWeight: "bold" }}>
                            Dọn dẹp dữ liệu
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Xóa lịch sử chơi game và reset giftcode về trạng thái ban đầu. Chú ý: các thao tác này không thể hoàn tác.
                        </Typography>

                        {!cleanupSupported ? (
                            <Alert severity="warning" sx={{ mb: 3 }}>
                                Server hiện tại chưa hỗ trợ cleanup API.
                            </Alert>
                        ) : null}

                        {!isSuperAdmin ? (
                            <Alert severity="error" sx={{ mb: 3 }}>
                                Bạn không có quyền thực hiện dọn dẹp dữ liệu. Chỉ tài khoản Super Admin (iduser = 1) mới có quyền sử dụng chức năng này.
                            </Alert>
                        ) : null}

                        {result && (
                            <Alert severity={result.type} onClose={() => setResult(null)} sx={{ mb: 3 }}>
                                {result.message}
                            </Alert>
                        )}

                        <Grid container spacing={3}>
                            {cards.map((card) => (
                                <Grid item xs={12} md={4} key={card.key}>
                                    <Card
                                        sx={{
                                            height: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            borderTop: `4px solid ${
                                                card.btnColor === "error"
                                                    ? "#f44336"
                                                    : card.btnColor === "warning"
                                                    ? "#ff9800"
                                                    : card.btnColor === "success"
                                                    ? "#4caf50"
                                                    : card.btnColor === "info"
                                                    ? "#2196f3"
                                                    : "#9c27b0"
                                            }`
                                        }}
                                    >
                                        <CardContent sx={{ flexGrow: 1 }}>
                                            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>{card.icon}</Box>
                                            <Typography variant="h6" align="center" sx={{ fontWeight: "bold", mb: 1 }}>
                                                {card.title}
                                            </Typography>
                                            <Divider sx={{ mb: 2 }} />
                                            <Typography variant="body2" color="text.secondary">
                                                {card.description}
                                            </Typography>
                                            {card.hasInput && (
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Nhập Username"
                                                    variant="outlined"
                                                    value={targetUsername}
                                                    onChange={(e) => setTargetUsername(e.target.value)}
                                                    sx={{ mt: 2 }}
                                                />
                                            )}
                                        </CardContent>
                                        <CardActions sx={{ p: 2 }}>
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                color={card.btnColor}
                                                disabled={loading[card.key] || !cleanupSupported || !isSuperAdmin}
                                                startIcon={loading[card.key] ? <CircularProgress size={16} color="inherit" /> : null}
                                                onClick={() => handleAction(card.key, card.label)}
                                            >
                                                {loading[card.key] ? "Đang xử lý..." : card.btnLabel}
                                            </Button>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Container>
                </Box>
            </DashboardLayout>
        </ThemeProvider>
    );
}

export default Cleanup;
